package com.karyadina.ui.screens.studyPlanner.presentation

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.karyadina.service.StudyPlannerServiceImpl
import com.karyadina.service.FlashcardServiceImpl
import com.karyadina.service.StudyFlashcardIntegration
import com.karyadina.service.FlashcardStorageImpl
import com.karyadina.service.GlobalTimerState
import com.karyadina.service.SessionTimerManager
import com.karyadina.service.TimerNotificationService
import com.karyadina.service.createTimerNotificationService
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.time.Clock // Use Kotlinx-Datetime for KMP
import kotlin.time.ExperimentalTime

import org.koin.core.component.KoinComponent
import org.koin.core.component.inject

class StudyPlannerViewModel : ViewModel(), KoinComponent {

    // Inject via Koin to share the singleton instance
    private val service: StudyPlannerServiceImpl by inject()
    private val sessionTimerManager: SessionTimerManager by inject()
    
    private val flashcardService = FlashcardServiceImpl(FlashcardStorageImpl())
    private val integration = StudyFlashcardIntegration(flashcardService, service)
    
    private val notificationService: TimerNotificationService = createTimerNotificationService(sessionTimerManager)

    private val _uiState = MutableStateFlow(StudyPlannerUiState())
    val uiState = _uiState.asStateFlow()

    init {
        loadData()
        observeTimer()
        notificationService.startMonitoring()

        // Observe storage refresh signals (e.g. from cloud sync)
        viewModelScope.launch {
            com.karyadina.service.AppStorage.refreshNotifier.collect {
                service.refreshFromStorage()
                loadData()
            }
        }
    }

    fun onEvent(event: StudyPlannerEvent) {
        when (event) {
            is StudyPlannerEvent.SelectTab -> _uiState.update { it.copy(selectedTab = event.index) }
            is StudyPlannerEvent.Refresh -> loadData()

            // Dialogs
            is StudyPlannerEvent.ToggleAddSessionDialog -> _uiState.update { it.copy(isAddSessionDialogOpen = event.isOpen) }
            is StudyPlannerEvent.ToggleAddSubjectDialog -> _uiState.update { it.copy(isAddSubjectDialogOpen = event.isOpen) }

            // Actions
            is StudyPlannerEvent.StartSession -> {
                // Get session details
                val session = service.getSession(event.sessionId)
                session?.let {
                    val duration = if (it.targetDurationMinutes > 0) it.targetDurationMinutes * 60L else 0L
                     sessionTimerManager.start(it.id, it.subject, duration)
                }
            }
            is StudyPlannerEvent.StopSession -> sessionTimerManager.stop()
            is StudyPlannerEvent.CompleteSession -> {
                service.completeSession(event.sessionId)
                loadData()
            }
            is StudyPlannerEvent.DeleteSession -> {
                service.deleteSession(event.sessionId)
                loadData()
            }
            is StudyPlannerEvent.DeleteSubject -> {
                service.deleteSubject(event.subjectId)
                loadData()
            }
            is StudyPlannerEvent.CreateSession -> {
                service.createSession(event.session)
                loadData()
                onEvent(StudyPlannerEvent.ToggleAddSessionDialog(false))
            }
            is StudyPlannerEvent.CreateSubject -> {
                service.saveSubject(event.subject)
                loadData()
                onEvent(StudyPlannerEvent.ToggleAddSubjectDialog(false))
            }
        }
    }

    private fun loadData() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true) }
            // Simulate fetching data
            val sessions = service.getTodaySessions()
            val subjects = service.getAllSubjects()
            val stats = service.getStatistics()
            val reviews = integration.getFlashcardReviewNeeds()
            val progressMap = subjects.associate {
                subject -> subject.id to service.getSubjectProgress(subject.id)
            }
            _uiState.update {
                it.copy(
                    isLoading = false,
                    sessions = sessions,
                    subjects = subjects,
                    stats = stats,
                    flashcardReviews = reviews,
                    subjectProgressMap = progressMap
                )
            }
        }
    }

    private fun observeTimer() {
        viewModelScope.launch {
            sessionTimerManager.timerState.collect { timerState ->
                when (timerState.state) {
                    GlobalTimerState.RUNNING, GlobalTimerState.PAUSED -> {
                        _uiState.update { 
                            it.copy(
                                activeSessionId = timerState.sessionId,
                                sessionElapsedTimeSeconds = timerState.elapsedTimeSeconds
                            ) 
                        }
                    }
                    GlobalTimerState.COMPLETED -> {
                         // Auto-stop/complete logic if needed, or just show completed state
                         timerState.sessionId?.let { id ->
                             // Mark as complete in DB? 
                             // sessionTimerManager.stop() // OR keep it in completed state until user dismisses
                             
                             // For now, let's just update UI time
                             _uiState.update { it.copy(sessionElapsedTimeSeconds = timerState.elapsedTimeSeconds) }
                         }
                    }
                    GlobalTimerState.IDLE -> {
                        // Timer stopped (handled by Manager), just update UI
                        _uiState.update { it.copy(activeSessionId = null, sessionElapsedTimeSeconds = 0) }
                        // Refresh data to reflect Manager's DB updates
                        loadData()
                    }
                }
            }
        }
    }

    private fun saveSessionProgress(sessionId: String, elapsedSeconds: Long) {
        val session = service.getSession(sessionId)
        if (session != null) {
            val elapsedMinutes = (elapsedSeconds / 60).toInt()
            if (elapsedMinutes > 0) {
                 val newActual = session.actualDurationMinutes + elapsedMinutes
                 val updatedSession = session.copy(
                    actualDurationMinutes = newActual,
                    completed = newActual >= session.targetDurationMinutes
                )
                service.updateSession(updatedSession)
                loadData()
            }
        }
    }
}